import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import { useState } from "react";

import AppIcon from "@/common/components/Icons/AppIcon";
import { EntityHelper } from "@/common/helpers/entity";
import { StringHelper } from "@/common/helpers/string";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useDisplayProps } from "@/common/hooks/useDisplayProps";
import { ContractDtoAll } from "@/common/mapping/mappers/contract";
import { ApiEnumName, enumService } from "@/common/services/enum";
import { apiClient } from "@/core/api/ApiClient";
import { ContractDto, ContractReferenceDto } from "@/core/api/generated/v1";

import MenuWithTrigger from "../../Menu/MenuWithTrigger";
import { getCustomerInlineTitleShort } from "../Customer/CustomerInline";
import { getVehicleInlineTitleShort } from "../Vehicle/VehicleInline";
import BaseEntityInline, {
  BaseEntityInlineInheritableProps,
} from "../components/EntityInline/BaseEntityInline";
import { EntityInlineActionIcon } from "../components/EntityInline/EntityInlineActionIcon";
import { EntityInlineActionIconButton } from "../components/EntityInline/EntityInlineActionIconButton";
import { EntityInlineActions } from "../components/EntityInline/EntityInlineActions";
import { ContractViewModal } from "./View/ContractViewModal";

const defaultDisplayProps = { vehicle: true, customer: true };
type DisplayProps = typeof defaultDisplayProps;

export const getContractInlineTitle = (data: Nil<ContractDtoAll>, displayProps?: DisplayProps) =>
  data
    ? StringHelper.join(
        [
          EntityHelper.getNumberTitle(data),
          enumService.getEnumValueName(ApiEnumName.ContractType, data.type),
          enumService.getEnumValueName(ApiEnumName.ContractStage, data.stageHistory?.stage),
          displayProps?.vehicle && data.vehicle
            ? getVehicleInlineTitleShort(data.vehicle)
            : undefined,
          displayProps?.customer && data.customer
            ? getCustomerInlineTitleShort(data.customer)
            : undefined,
        ],
        ", ",
        {
          skipEmpty: true,
        },
      )
    : undefined;

export const getContractInlineTitleShort = (data: Nil<ContractDtoAll>) =>
  data &&
  StringHelper.join(
    [
      EntityHelper.getNumberTitle(data),
      enumService.getEnumValueName(ApiEnumName.ContractType, data.type),
    ],
    ", ",
    {
      skipEmpty: true,
    },
  );

export interface ContractInlineProps extends BaseEntityInlineInheritableProps {
  entity: ContractDto | ContractReferenceDto | null | undefined;
  entityId?: string | null;
  displayProps?: Partial<DisplayProps>;
}

/** Inline brief information about the contract. */
export default function ContractInline({
  entity,
  entityId,
  displayProps,
  fetch = true,
  withActions,
  withMenu,
  viewProps,
  ...otherProps
}: ContractInlineProps) {
  const displayPropsComputed = useDisplayProps([defaultDisplayProps, displayProps]);

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const request = useApiRequest(
    apiClient.contractsApi.apiV1ContractsContractIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      contractId: entityId!,
    },
    {
      deps: [entityId],
      skip: !fetch || !entityId || !!entity,
    },
  );
  const refRequest = useApiRequest(
    apiClient.contractsApi.apiV1ContractsContractIdReferenceGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      contractId: entityId!,
    },
    {
      deps: [entityId],
      skip: !fetch || !entityId || !!entity || (request.isLoading && !request.isEnded),
    },
  );
  entity = request?.data || entity || refRequest?.data;

  const entityIdComputed = entity?.id || entityId;

  return (
    <>
      <BaseEntityInline
        entity={entity}
        entityId={entityId}
        request={fetch ? (request.data ? request : refRequest) : undefined}
        icon={<AppIcon of='contract' inText />}
        content={getContractInlineTitle(entity, displayPropsComputed) || "Contract"}
        defaultTooltipTitle='Contract'
        withActions={withActions}
        withMenu={withMenu}
        actions={
          !withActions ? undefined : (
            <EntityInlineActions>
              <EntityInlineActionIconButton
                onClick={() => setIsViewModalOpen(true)}
                tooltipProps={{ title: "Quick view" }}
              >
                <EntityInlineActionIcon of='quickView' />
              </EntityInlineActionIconButton>
            </EntityInlineActions>
          )
        }
        menu={
          !withMenu ? undefined : (
            <MenuWithTrigger
              withAuthCloseOnClick
              trigger={
                <EntityInlineActionIconButton>
                  <EntityInlineActionIcon of='moreVert' />
                </EntityInlineActionIconButton>
              }
            >
              <MenuItem dense onClick={() => setIsViewModalOpen(true)}>
                <ListItemIcon>
                  <AppIcon of='quickView' fontSize='small' />
                </ListItemIcon>
                <ListItemText>Quick view</ListItemText>
              </MenuItem>
            </MenuWithTrigger>
          )
        }
        {...otherProps}
      />

      {(withActions || withMenu) && (
        <>
          {/* View modal */}
          {entityIdComputed && (
            <ContractViewModal
              open={isViewModalOpen}
              onClose={() => setIsViewModalOpen(false)}
              viewProps={{
                ...viewProps,
                contractId: entityIdComputed,
                contract: EntityHelper.nullIfNotFull(entity),
                onUpdate: viewProps?.onUpdate || null,
                onDelete: viewProps?.onDelete || null,
              }}
            />
          )}
        </>
      )}
    </>
  );
}
